import {
  ReconciliationCommissionAdjustment,
  ReconciliationOrderItem,
  ReconciliationSupplierOrder,
} from 'apis/reconciliation';
import dayjs from 'dayjs';

/** One editable line of the Adjust order modal — string inputs so the user can type freely;
 * `computeAdjustedTotal` parses them. */
export interface AdjustLineDraft {
  orderItemId: number;
  nameEn: string;
  unit: string;
  originalQuantity: number;
  originalPrice: number;
  quantity: string;
  price: string;
  isCancelled: boolean;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

const toNumber = (value: string): number => {
  const n = Number(value);
  return value.trim() === '' || Number.isNaN(n) ? 0 : n;
};

/** Seeds the modal from the current (possibly already adjusted) lines. Originals fall back to the
 * effective values for a pre-adjustment BE that does not send them. */
export const toLineDrafts = (items: ReconciliationOrderItem[]): AdjustLineDraft[] =>
  items
    .filter((item): item is ReconciliationOrderItem & { orderItemId: number } => item.orderItemId != null)
    .map((item) => ({
      orderItemId: item.orderItemId,
      nameEn: item.nameEn,
      unit: item.unit,
      originalQuantity: item.originalQuantity ?? item.quantity,
      originalPrice: item.originalPrice ?? item.price,
      quantity: String(item.isCancelled ? item.originalQuantity ?? item.quantity : item.quantity),
      price: String(item.price),
      isCancelled: !!item.isCancelled,
    }));

/** Σ non-cancelled qty × price, 2dp — the preview of what the BE derives when no total override
 * is typed. Cancelled lines contribute nothing regardless of their inputs. */
export const computeAdjustedTotal = (lines: AdjustLineDraft[]): number =>
  round2(lines.filter((l) => !l.isCancelled).reduce((sum, l) => sum + toNumber(l.quantity) * toNumber(l.price), 0));

/** Σ non-cancelled quantities (3dp, the order_items precision). */
export const computeAdjustedQuantity = (lines: AdjustLineDraft[]): number =>
  Math.round(lines.filter((l) => !l.isCancelled).reduce((sum, l) => sum + toNumber(l.quantity), 0) * 1000) / 1000;

/** Per-line validation message, or null when the line is fine. Cancelled lines are never invalid. */
export const getLineError = (line: AdjustLineDraft): string | null => {
  if (line.isCancelled) return null;
  const qty = Number(line.quantity);
  const price = Number(line.price);
  if (line.quantity.trim() === '' || Number.isNaN(qty) || qty < 0) return 'Quantity must be 0 or more';
  if (!/^\d*(\.\d{0,3})?$/.test(line.quantity)) return 'Quantity: at most 3 decimals';
  if (line.price.trim() === '' || Number.isNaN(price) || price < 0) return 'Price must be 0 or more';
  if (!/^\d*(\.\d{0,3})?$/.test(line.price)) return 'Price: at most 3 decimals';
  return null;
};

/** True when the line differs from the platform order line (edited or cancelled). */
export const isLineChanged = (line: AdjustLineDraft): boolean =>
  line.isCancelled || toNumber(line.quantity) !== line.originalQuantity || toNumber(line.price) !== line.originalPrice;

/** "Adjusted" badge tooltip: reason · by · date. */
export const getAdjustmentTooltip = (order: ReconciliationSupplierOrder): string | null => {
  const adj = order.adjustment;
  if (!adj) return null;
  const parts = [
    adj.isTotalOverridden ? `Total typed by ops — ${adj.reason}` : adj.reason,
    adj.adjustedByName ? `by ${adj.adjustedByName}` : null,
    adj.adjustedAt ? dayjs(adj.adjustedAt).format('DD MMM YYYY') : null,
  ].filter(Boolean);
  return parts.join(' · ');
};

/** The invoice was rendered at a different commission than the order carries now (order edited,
 * adjusted, or terms changed after a paid invoice — paid invoices are never re-rendered). Best
 * effort: a pre-adjustment BE sends no `netAmount`, so it can never be stale then. */
export const isOrderInvoiceStale = (order: Pick<ReconciliationSupplierOrder, 'commission' | 'invoice'>): boolean => {
  const net = order.invoice?.netAmount;
  if (net == null || order.commission == null) return false;
  return Math.abs(net - order.commission) > 0.01;
};

/** Human summary of a month-level commission adjustment for tooltips/labels. */
export const getCommissionAdjustmentLabel = (
  adj: ReconciliationCommissionAdjustment | null | undefined
): string | null => {
  if (!adj) return null;
  if (adj.waived) return `Commission waived${adj.reason ? ` — ${adj.reason}` : ''}`;
  if (adj.amount == null) return null;
  const sign = adj.amount > 0 ? '+' : '−';
  return `Adjusted ${sign}${Math.abs(adj.amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}${adj.reason ? ` — ${adj.reason}` : ''}`;
};
